import {
	Box,
	Divider,
	Grid2,
	LinearProgress,
	Paper,
	Typography,
} from "@mui/material";
import {
	ALL_SECTIONS,
	EMPTY,
	type DetailSection,
	type RollDetail,
	formatText,
} from "../models/RollTurnHistoryDetail";

const sectionTitleSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.78rem",
	letterSpacing: "0.04em",
	textTransform: "uppercase" as const,
};

/**
 * Label above, value below. The six sections sit side by side on a wide screen, so a
 * `Label: value` on one line would not survive a column one sixth of the page wide.
 */
const InfoField = ({
	label,
	value,
	help,
}: {
	label: string;
	value: string;
	help: string;
}) => (
	<Box sx={{ py: 0.15 }} title={help}>
		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ display: "block", lineHeight: 1.3 }}
		>
			{label}
		</Typography>
		<Typography
			variant="body2"
			sx={{
				fontWeight: 600,
				lineHeight: 1.4,
				fontVariantNumeric: "tabular-nums",
				wordBreak: "break-word",
			}}
		>
			{value}
		</Typography>
	</Box>
);

/**
 * Percent remaining decides whether the roll keeps working or gets scrapped, so it is the
 * one number on the sheet that earns a shape instead of another line of text.
 */
const RemainingLife = ({ percent }: { percent: number | null }) => {
	if (percent == null) {
		return (
			<InfoField
				label="Percent Remaining"
				value={EMPTY}
				help="Share of the usable diameter still left"
			/>
		);
	}
	const clamped = Math.max(0, Math.min(100, percent));
	return (
		<Box
			sx={{ py: 0.15 }}
			title="Share of the usable diameter still left, between the new and the scrap diameter"
		>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", lineHeight: 1.3 }}
			>
				Percent Remaining
			</Typography>
			<Typography
				variant="body2"
				sx={{
					fontWeight: 700,
					lineHeight: 1.4,
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{percent.toFixed(2)} %
			</Typography>
			<LinearProgress
				variant="determinate"
				value={clamped}
				color={clamped < 20 ? "error" : clamped < 40 ? "warning" : "success"}
				sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
			/>
		</Box>
	);
};

const SectionColumn = ({
	section,
	roll,
}: {
	section: DetailSection;
	roll: RollDetail;
}) => (
	<Box>
		<Typography sx={sectionTitleSx}>{section.title}</Typography>
		<Divider sx={{ mt: 0.25, mb: 0.5 }} />
		{section.fields.map((field) => (
			<InfoField
				key={field.label}
				label={field.label}
				value={field.value(roll)}
				help={field.help}
			/>
		))}
		{section.title === "Geometry" && (
			<RemainingLife percent={roll.CurrentPercentageLeft} />
		)}
	</Box>
);

interface Props {
	roll: RollDetail;
}

export const RollHeaderPanel = ({ roll }: Props) => (
	<Paper
		elevation={3}
		sx={{ px: 2, pt: 2.5, pb: 2, height: "100%", overflow: "auto" }}
	>
		<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
			Roll Foundry Number: {formatText(roll.Name)}
		</Typography>
		<Grid2 container columnSpacing={2} rowSpacing={1}>
			{ALL_SECTIONS.map((section) => (
				<Grid2 key={section.title} size={{ xs: 12, sm: 6, md: 4 }}>
					<SectionColumn section={section} roll={roll} />
				</Grid2>
			))}
		</Grid2>
		<Divider sx={{ mt: 1.5, mb: 0.5 }} />
		<InfoField
			label="Comments"
			value={formatText(roll.Comments)}
			help="Free text kept on the roll record"
		/>
	</Paper>
);
